import { Outlet, useLocation } from 'react-router';
import { useAuth } from '@/contexts/AuthContext';
import { useAccountStatus } from '@/hooks/queries/useAccountStatus';
import { BootSplash } from '@/components/BootSplash';
import { CrossHostNavigate } from '@/components/CrossHostNavigate';

// Owner paths that should NOT trigger the blocked-user redirect. Each path is
// matched as a suffix so we accept both prod ("/subscription") and local mode
// ("/owner/subscription") without duplicating entries.
const BLOCKED_ALLOWED_PATHS = ['/subscription', '/support'];

export function AuthGuard() {
  const { isAuthenticated, isLoading, user } = useAuth();
  const { data: accountStatus } = useAccountStatus();
  const location = useLocation();

  if (isLoading) {
    return <BootSplash />;
  }

  if (!isAuthenticated) {
    // Owner login lives on the marketing host (velko-pos.com/login).
    return <CrossHostNavigate to="/login" />;
  }

  const pathname = location.pathname;

  if (
    user &&
    !user.is_admin &&
    (user.shops?.length ?? 0) === 0 &&
    !pathname.endsWith('/onboarding')
  ) {
    return <CrossHostNavigate to="/owner/onboarding" />;
  }

  // Redirect blocked users to plans page (except allowed routes).
  // Skip this check for users without any shop — they must onboard first and
  // have no subscription state yet (avoids /onboarding ↔ /subscription/plans loop).
  if (accountStatus && user && (user.shops?.length ?? 0) > 0) {
    const isBlocked =
      accountStatus.access_state === 'trial_expired' || accountStatus.access_state === 'blocked';
    const onAllowedPath = BLOCKED_ALLOWED_PATHS.some(
      (p) => pathname.startsWith(p) || pathname.startsWith(`/owner${p}`),
    );

    if (isBlocked && !onAllowedPath) {
      return <CrossHostNavigate to="/owner/subscription/plans" />;
    }
  }

  return <Outlet />;
}
